/**
 * Service Supabase Realtime pour les messages
 * Permet de recevoir les messages en temps réel sans polling
 */

import { createClient } from '@supabase/supabase-js'

let supabaseClient = null
let subscriptions = new Map()

/**
 * Initialise le client Supabase Realtime
 * @param {string} supabaseUrl - URL de votre projet Supabase
 * @param {string} supabaseAnonKey - Clé anonyme de Supabase
 * @param {string} authToken - Token d'authentification de l'utilisateur
 */
export function initSupabaseRealtime(supabaseUrl, supabaseAnonKey, authToken) {
  try {
    if (!supabaseUrl || !supabaseAnonKey) {
      console.warn('[Realtime] URL ou clé Supabase manquante')
      return
    }

    // Si le client existe déjà, mettre à jour le token si nécessaire
    if (supabaseClient) {
      if (authToken) {
        // Mettre à jour la session avec le nouveau token
        supabaseClient.auth.setSession({
          access_token: authToken,
          refresh_token: '',
          expires_in: 3600,
          token_type: 'bearer',
          user: null
        }).then(() => {
          // Mettre à jour également l'authentification Realtime
          if (supabaseClient.realtime && typeof supabaseClient.realtime.setAuth === 'function') {
            try {
              supabaseClient.realtime.setAuth(authToken)
              console.log('[Realtime] ✅ Token Realtime mis à jour via setAuth()')
            } catch (err) {
              console.warn('[Realtime] ⚠️ Erreur lors de la mise à jour du token Realtime:', err)
            }
          }
        }).catch(err => {
          console.warn('[Realtime] Erreur lors de la mise à jour de la session:', err)
        })
      }
      console.log('[Realtime] Client Supabase déjà initialisé - Token mis à jour si nécessaire')
      return
    }

    // Créer un nouveau client uniquement s'il n'existe pas
    // Configurer le client avec le token dans les headers Realtime
    const realtimeParams = {
      eventsPerSecond: 10
    }
    
    // Ajouter le token dans les headers si disponible
    if (authToken) {
      realtimeParams.headers = {
        Authorization: `Bearer ${authToken}`
      }
    }
    
    supabaseClient = createClient(supabaseUrl, supabaseAnonKey, {
      auth: {
        persistSession: false,
        autoRefreshToken: false,
      },
      realtime: {
        params: realtimeParams
      }
    })
    
    // Configurer la session auth pour les requêtes REST
    if (authToken) {
      supabaseClient.auth.setSession({
        access_token: authToken,
        refresh_token: '',
        expires_in: 3600,
        token_type: 'bearer',
        user: null
      }).then(() => {
        console.log('[Realtime] ✅ Session auth configurée avec succès')
        // Configurer également l'authentification Realtime directement
        if (supabaseClient.realtime && typeof supabaseClient.realtime.setAuth === 'function') {
          try {
            supabaseClient.realtime.setAuth(authToken)
            console.log('[Realtime] ✅ Authentification Realtime configurée via setAuth()')
          } catch (err) {
            console.warn('[Realtime] ⚠️ setAuth() non disponible, utilisation de setSession uniquement:', err)
          }
        } else {
          console.log('[Realtime] ℹ️ setAuth() non disponible, utilisation de setSession uniquement')
        }
      }).catch(err => {
        console.warn('[Realtime] ⚠️ Erreur lors de la configuration de la session:', err)
      })
    }
    
    console.log('[Realtime] Client Supabase créé avec succès')
  } catch (err) {
    console.warn('[Realtime] Erreur lors de l\'initialisation:', err)
    supabaseClient = null
  }
}

/**
 * S'abonne aux nouveaux messages pour une conversation
 * @param {string} userId - ID de l'utilisateur actuel
 * @param {string} otherUserId - ID de l'autre utilisateur dans la conversation
 * @param {Function} onNewMessage - Callback appelé quand un nouveau message arrive
 * @returns {Function} Fonction pour se désabonner
 */
export function subscribeToMessages(userId, otherUserId, onNewMessage) {
  if (!supabaseClient) {
    console.warn('[Realtime] Client Supabase non initialisé')
    return () => {}
  }

  const subscriptionKey = `${userId}-${otherUserId}`
  
  // Se désabonner si déjà abonné
  if (subscriptions.has(subscriptionKey)) {
    unsubscribeFromMessages(userId, otherUserId)
  }

  try {
    // S'abonner aux INSERT sur la table messages
    // Note: Supabase Realtime ne supporte pas les filtres OR complexes
    // On s'abonne à tous les INSERT et on filtre dans le callback
    const channel = supabaseClient
      .channel(`messages:${subscriptionKey}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'messages'
          // Pas de filtre ici - Supabase Realtime ne supporte pas les filtres OR
          // Le filtrage se fait dans le callback ci-dessous
        },
        (payload) => {
          const message = payload.new
          console.log('%c[Realtime] 📨 ÉVÉNEMENT REALTIME REÇU (INSERT)', 'color: #00ff00; font-weight: bold; font-size: 12px;', {
            message_id: message.id,
            sender_id: message.sender_id,
            receiver_id: message.receiver_id,
            current_user: userId,
            other_user: otherUserId,
            content: message.content?.substring(0, 50) + '...',
            timestamp: new Date().toISOString()
          })
          
          // Vérifier que le message appartient bien à cette conversation
          const isRelevant = 
            (message.sender_id === userId && message.receiver_id === otherUserId) ||
            (message.sender_id === otherUserId && message.receiver_id === userId)
          
          if (isRelevant) {
            console.log('%c[Realtime] ✅ Message pertinent - Ajout immédiat à la conversation (SANS POLLING)', 'color: #00ff00; font-weight: bold;')
            onNewMessage(message)
          } else {
            console.log('[Realtime] ⏭️ Message ignoré (ne concerne pas cette conversation)', {
              message_sender: message.sender_id,
              message_receiver: message.receiver_id,
              expected_sender: userId,
              expected_receiver: otherUserId
            })
          }
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'messages'
          // Pas de filtre ici - le filtrage se fait dans le callback
        },
        (payload) => {
          const message = payload.new
          console.log('[Realtime] 🔄 Message mis à jour via Realtime:', message.id)
          
          // Mettre à jour le statut de lecture
          const isRelevant = 
            (message.sender_id === userId && message.receiver_id === otherUserId) ||
            (message.sender_id === otherUserId && message.receiver_id === userId)
          
          if (isRelevant && message.read_at) {
            console.log('[Realtime] ✅ Mise à jour pertinente - Statut de lecture')
            onNewMessage(message, 'update')
          }
        }
      )
      .subscribe((status) => {
        if (status === 'SUBSCRIBED') {
          console.log('[Realtime] ✅ Abonné aux messages pour', subscriptionKey)
          console.log('[Realtime] 📡 Connexion WebSocket active - Messages en temps réel activés')
          console.log('[Realtime] 🔍 Filtrage côté client pour la conversation:', subscriptionKey)
        } else if (status === 'CHANNEL_ERROR') {
          console.error('[Realtime] ❌ Erreur d\'abonnement pour', subscriptionKey)
          console.error('[Realtime] ⚠️ Vérifiez que la table messages est dans la publication supabase_realtime')
        } else if (status === 'TIMED_OUT') {
          console.warn('[Realtime] ⏱️ Timeout lors de l\'abonnement pour', subscriptionKey)
        } else if (status === 'CLOSED') {
          console.warn('[Realtime] 🔌 Connexion fermée pour', subscriptionKey)
        } else {
          console.log('[Realtime] 📊 Statut de l\'abonnement:', status)
        }
      })

    subscriptions.set(subscriptionKey, channel)
    
    return () => unsubscribeFromMessages(userId, otherUserId)
  } catch (error) {
    console.error('[Realtime] Erreur lors de l\'abonnement:', error)
    return () => {}
  }
}

/**
 * Se désabonner des messages pour une conversation
 */
export function unsubscribeFromMessages(userId, otherUserId) {
  const subscriptionKey = `${userId}-${otherUserId}`
  const channel = subscriptions.get(subscriptionKey)
  
  if (channel && supabaseClient) {
    supabaseClient.removeChannel(channel)
    subscriptions.delete(subscriptionKey)
    console.log('[Realtime] Désabonné des messages pour', subscriptionKey)
  }
}

/**
 * Se désabonner de toutes les conversations
 */
export function unsubscribeAll() {
  subscriptions.forEach((channel) => {
    if (supabaseClient) {
      supabaseClient.removeChannel(channel)
    }
  })
  subscriptions.clear()
  console.log('[Realtime] Tous les abonnements ont été annulés')
}

/**
 * Vérifie si Realtime est disponible
 */
export function isRealtimeAvailable() {
  return supabaseClient !== null
}

